import { useEffect, useState } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Activity, RefreshCw, Globe, Server, CheckCircle2, AlertTriangle, X, Clock, Cpu, AlertOctagon, MonitorPlay } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { NewBadge } from '@/components/NewBadge';

const TONE = {
  ok: { bg: 'bg-emerald-500/10', text: 'text-emerald-700 dark:text-emerald-400', label: 'OK' },
  redirect: { bg: 'bg-sky-500/10', text: 'text-sky-700 dark:text-sky-400', label: 'Redirect' },
  auth: { bg: 'bg-violet-500/10', text: 'text-violet-700 dark:text-violet-400', label: 'Auth' },
  thin: { bg: 'bg-amber-500/15', text: 'text-amber-700 dark:text-amber-400', label: 'Thin body' },
  not_found: { bg: 'bg-rose-500/15', text: 'text-rose-700 dark:text-rose-400', label: '404' },
  server_error: { bg: 'bg-rose-600/15', text: 'text-rose-700 dark:text-rose-400', label: '5xx' },
  error: { bg: 'bg-rose-700/15', text: 'text-rose-700 dark:text-rose-400', label: 'Error' },
  blank_browser: { bg: 'bg-rose-600/15', text: 'text-rose-700 dark:text-rose-400', label: 'Blank' },
  skipped: { bg: 'bg-stone-400/15', text: 'text-stone-600 dark:text-stone-400', label: 'Skipped' },
  unknown: { bg: 'bg-stone-500/10', text: 'text-stone-700 dark:text-stone-400', label: 'Unknown' },
};

const ResultPill = ({ classification, status }) => {
  const t = TONE[classification] || TONE.unknown;
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold ${t.bg} ${t.text}`}>
      {classification === 'ok' && <CheckCircle2 size={10} />}
      {(classification === 'not_found' || classification === 'server_error' || classification === 'error') && <X size={10} />}
      {classification === 'thin' && <AlertTriangle size={10} />}
      {t.label} · {status || '—'}
    </span>
  );
};

export default function RouteHealthPage() {
  const [latest, setLatest] = useState(null);
  const [history, setHistory] = useState([]);
  const [running, setRunning] = useState(false);
  const [browserRunning, setBrowserRunning] = useState(false);
  const [browserLatest, setBrowserLatest] = useState(null);
  const [schedStatus, setSchedStatus] = useState(null);
  const [renderErrors, setRenderErrors] = useState(null);

  const loadAll = async () => {
    try {
      const [l, h, s, re, bl] = await Promise.all([
        api.get('/admin/route-health/latest'),
        api.get('/admin/route-health/history?limit=10'),
        api.get('/admin/scheduler-status').catch(() => ({ data: null })),
        api.get('/admin/route-health/render-errors?limit=30').catch(() => ({ data: null })),
        api.get('/admin/route-health/browser-latest').catch(() => ({ data: null })),
      ]);
      setLatest(l.data?.never_run ? null : l.data);
      setHistory(h.data?.runs || []);
      setSchedStatus(s.data);
      setRenderErrors(re.data);
      setBrowserLatest(bl.data?.never_run ? null : bl.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load route health'); }
  };

  const runProbe = async () => {
    setRunning(true);
    try {
      const r = await api.post('/admin/route-health/probe');
      setLatest(r.data);
      toast.success(r.data.bad_count === 0 ? 'All routes healthy' : `${r.data.bad_count} route(s) need attention`);
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Probe failed'); }
    finally { setRunning(false); }
  };

  const runBrowserProbe = async (mode = 'public') => {
    setBrowserRunning(true);
    try {
      const r = await api.post(`/admin/route-health/browser-probe?mode=${mode}`);
      setBrowserLatest(r.data);
      const m = r.data.mode || mode;
      const label = m === 'auth' ? 'Authenticated' : m === 'both' ? 'Public + Auth' : 'Public';
      toast.success(
        r.data.bad_count === 0
          ? `${label} probe: ${r.data.totals?.ok || 0}/${r.data.totals?.total || 0} routes rendered cleanly`
          : `${label} probe: ${r.data.bad_count} route(s) failed headless render`
      );
    } catch (e) { toast.error(e.response?.data?.detail || 'Headless probe failed'); }
    finally { setBrowserRunning(false); }
  };

  useEffect(() => { loadAll(); }, []);

  return (
    <DashboardLayout>
      <div className="space-y-4 p-4 max-w-6xl mx-auto" data-testid="route-health-page">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-sky-500 to-cyan-500 flex items-center justify-center">
              <Activity size={18} className="text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold font-outfit flex items-center gap-2">
                Route Health Beacon
                <NewBadge since="2026-04-25" featureId="page-route-health" size="xs" />
              </h1>
              <p className="text-xs text-muted-foreground">HTTP-level probe of every critical SPA + API route. Catches 404s, 5xxs, and broken redirects within seconds.</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="inline-flex rounded-xl border border-violet-200 dark:border-violet-800 overflow-hidden">
              <Button
                onClick={() => runBrowserProbe('public')}
                disabled={browserRunning}
                variant="ghost"
                className="rounded-none bg-violet-50 hover:bg-violet-100 text-violet-700 dark:bg-violet-950/20 dark:text-violet-300 border-r border-violet-200 dark:border-violet-800 px-3"
                data-testid="browser-probe-btn"
                title="Render public routes only — no login required"
              >
                <MonitorPlay size={13} className={`mr-1.5 ${browserRunning ? 'animate-pulse' : ''}`} />
                {browserRunning ? 'Rendering…' : 'Headless probe'}
              </Button>
              <Button
                onClick={() => runBrowserProbe('auth')}
                disabled={browserRunning}
                variant="ghost"
                className="rounded-none bg-violet-50 hover:bg-violet-100 text-violet-700 dark:bg-violet-950/20 dark:text-violet-300 border-r border-violet-200 dark:border-violet-800 px-2.5 text-xs"
                data-testid="browser-probe-auth-btn"
                title="Render authenticated routes (uses your session token)"
              >
                Auth
              </Button>
              <Button
                onClick={() => runBrowserProbe('both')}
                disabled={browserRunning}
                variant="ghost"
                className="rounded-none bg-violet-50 hover:bg-violet-100 text-violet-700 dark:bg-violet-950/20 dark:text-violet-300 px-2.5 text-xs"
                data-testid="browser-probe-both-btn"
                title="Render both public and authenticated routes"
              >
                Both
              </Button>
            </div>
            <Button onClick={runProbe} disabled={running} className="bg-sky-600 hover:bg-sky-700" data-testid="probe-now-btn">
              <RefreshCw size={13} className={`mr-1.5 ${running ? 'animate-spin' : ''}`} />
              {running ? 'Probing…' : 'Probe now'}
            </Button>
          </div>
        </div>

        {!latest ? (
          <Card>
            <CardContent className="p-10 text-center">
              <p className="text-sm text-muted-foreground mb-3">No probes have run yet.</p>
              <Button onClick={runProbe} disabled={running} className="bg-sky-600 hover:bg-sky-700">Run first probe</Button>
            </CardContent>
          </Card>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="route-health-summary">
              <Card><CardContent className="p-3">
                <div className="text-[10px] uppercase font-semibold text-muted-foreground">Bad routes</div>
                <div className={`text-2xl font-bold mt-0.5 ${latest.bad_count === 0 ? 'text-emerald-600' : 'text-rose-600'}`}>{latest.bad_count}</div>
              </CardContent></Card>
              <Card><CardContent className="p-3">
                <div className="text-[10px] uppercase font-semibold text-muted-foreground">FE OK</div>
                <div className="text-2xl font-bold mt-0.5 text-foreground">{latest.totals?.ok_fe}/{latest.totals?.fe}</div>
              </CardContent></Card>
              <Card><CardContent className="p-3">
                <div className="text-[10px] uppercase font-semibold text-muted-foreground">API OK</div>
                <div className="text-2xl font-bold mt-0.5 text-foreground">{latest.totals?.ok_api}/{latest.totals?.api}</div>
              </CardContent></Card>
              <Card><CardContent className="p-3">
                <div className="text-[10px] uppercase font-semibold text-muted-foreground inline-flex items-center gap-1"><Clock size={10} />Last run</div>
                <div className="text-xs font-medium mt-1.5">{latest.ran_at ? new Date(latest.ran_at).toLocaleString() : '—'}</div>
                <div className="text-[10px] text-muted-foreground">by {latest.ran_by || '—'}</div>
              </CardContent></Card>
            </div>

            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm font-outfit flex items-center gap-2"><Globe size={14} className="text-sky-600" />Frontend routes</CardTitle></CardHeader>
              <CardContent className="p-0">
                <div className="divide-y">
                  {latest.frontend_routes?.map((r) => (
                    <div key={r.path} className="flex items-center justify-between px-3 py-2 hover:bg-muted/40 text-xs" data-testid={`fe-row-${r.path}`}>
                      <div className="flex-1 min-w-0">
                        <div className="font-mono">{r.path}</div>
                        <div className="text-[10px] text-muted-foreground">{r.body_len?.toLocaleString()} bytes · {r.ms || 0}ms</div>
                      </div>
                      <ResultPill classification={r.classification} status={r.status} />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm font-outfit flex items-center gap-2"><Server size={14} className="text-emerald-600" />API endpoints</CardTitle></CardHeader>
              <CardContent className="p-0">
                <div className="divide-y">
                  {latest.api_endpoints?.map((r) => (
                    <div key={r.path} className="flex items-center justify-between px-3 py-2 hover:bg-muted/40 text-xs">
                      <div className="flex-1 min-w-0">
                        <div className="font-mono">{r.path}</div>
                        <div className="text-[10px] text-muted-foreground">{r.body_len?.toLocaleString()} bytes · {r.ms || 0}ms</div>
                      </div>
                      <ResultPill classification={r.classification} status={r.status} />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Headless-browser probe results — actual SPA render with
                Chromium. Catches client-side blank pages and JS crashes
                that the HTTP probe (which only sees the SPA shell) misses. */}
            {browserLatest && (
              <Card data-testid="browser-probe-results">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-outfit flex items-center gap-2">
                    <MonitorPlay size={14} className="text-violet-600" />
                    Headless render check
                    <Badge variant="outline" className="ml-1 text-[9px] font-normal text-muted-foreground border-stone-200">
                      {browserLatest.totals?.ok || 0}/{browserLatest.totals?.total || 0} ok · {browserLatest.totals?.blank || 0} blank · {browserLatest.totals?.errors || 0} errors
                    </Badge>
                    <span className="ml-auto text-[10px] text-muted-foreground font-normal">
                      {browserLatest.ran_at ? new Date(browserLatest.ran_at).toLocaleString() : '—'}
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="divide-y">
                    {(browserLatest.results || []).map((r) => (
                      <div key={`${r.mode || 'public'}-${r.path}`} className="px-3 py-2 hover:bg-muted/40 text-xs" data-testid={`browser-row-${r.path}`}>
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="font-mono flex items-center gap-1.5">
                              {r.mode === 'auth' && (
                                <span className="px-1 py-px rounded bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300 text-[8px] font-bold uppercase tracking-wider">auth</span>
                              )}
                              <span>{r.path}</span>
                            </div>
                            <div className="text-[10px] text-muted-foreground">
                              {r.text_len?.toLocaleString() || 0} chars rendered
                              {r.title ? ` · "${r.title}"` : ''}
                              {r.console_error_count > 0 ? ` · ${r.console_error_count} console error(s)` : ''}
                            </div>
                          </div>
                          <ResultPill classification={r.classification} status={r.status} />
                        </div>
                        {(r.console_errors || []).length > 0 && (
                          <details className="mt-1">
                            <summary className="text-[10px] text-rose-600 cursor-pointer hover:underline">View {r.console_errors.length} error(s)</summary>
                            <div className="mt-1 space-y-0.5">
                              {r.console_errors.map((ce, i) => (
                                <div key={i} className="font-mono text-[10px] text-rose-700 bg-rose-50 dark:bg-rose-950/20 px-2 py-1 rounded break-all">{ce}</div>
                              ))}
                            </div>
                          </details>
                        )}
                        {r.error && (
                          <div className="mt-1 font-mono text-[10px] text-rose-700 bg-rose-50 dark:bg-rose-950/20 px-2 py-1 rounded">{r.error}</div>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {history.length > 1 && (
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm font-outfit">Recent runs</CardTitle></CardHeader>
                <CardContent className="p-0">
                  <div className="divide-y">
                    {history.map((h, i) => (
                      <div key={i} className="flex items-center justify-between px-3 py-2 text-xs">
                        <span className="text-muted-foreground">{new Date(h.ran_at).toLocaleString()}</span>
                        <Badge variant="outline" className={h.bad_count === 0 ? 'text-emerald-600 border-emerald-200' : 'text-rose-600 border-rose-200'}>
                          {h.bad_count} bad
                        </Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Scheduler status + last errors panel (iter333) */}
            {schedStatus && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="scheduler-status">
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm font-outfit flex items-center gap-2"><Cpu size={14} className="text-violet-600" />Background schedulers</CardTitle></CardHeader>
                  <CardContent className="p-0">
                    <div className="divide-y">
                      {(schedStatus.schedulers || []).map((s) => (
                        <div key={s.name} className="flex items-center justify-between px-3 py-2 text-xs" data-testid={`sched-${s.name}`}>
                          <div>
                            <div className="font-mono">{s.name}</div>
                            {s.env_flag && (
                              <div className="text-[10px] text-muted-foreground">env: {s.env_flag}=1 to enable</div>
                            )}
                          </div>
                          <Badge variant="outline" className={s.enabled ? 'text-emerald-600 border-emerald-200' : 'text-stone-500 border-stone-200'}>
                            {s.enabled ? 'enabled' : 'disabled'}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-outfit flex items-center gap-2">
                      <AlertOctagon size={14} className={schedStatus.error_count > 0 ? 'text-rose-600' : 'text-emerald-600'} />
                      Last errors {schedStatus.error_count > 0 && <span className="ml-1 text-xs text-rose-600">({schedStatus.error_count})</span>}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    {(schedStatus.recent_errors || []).length === 0 ? (
                      <div className="px-3 py-6 text-center text-xs text-muted-foreground">No errors recorded — schedulers are healthy.</div>
                    ) : (
                      <div className="divide-y max-h-[280px] overflow-y-auto">
                        {(schedStatus.recent_errors || []).map((er, i) => (
                          <div key={i} className="px-3 py-2 text-[11px]" data-testid={`sched-error-${i}`}>
                            <div className="flex items-center justify-between">
                              <span className="font-mono font-bold text-rose-700">{er.scheduler}</span>
                              <span className="text-[10px] text-muted-foreground">{new Date(er.ts).toLocaleTimeString()}</span>
                            </div>
                            <div className="font-mono text-rose-600 mt-0.5 break-all">{er.error}</div>
                            {er.context && <div className="text-[10px] text-muted-foreground mt-0.5">ctx: {er.context}</div>}
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}
            {/* Client-side render errors panel — catches blank pages caused
                by JS crashing on mount, which the HTTP probe cannot see. */}
            {renderErrors && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-outfit flex items-center gap-2">
                    <AlertOctagon size={14} className={renderErrors.count > 0 ? 'text-rose-600' : 'text-emerald-600'} />
                    Client-side render errors (last 7 days) {renderErrors.count > 0 && <span className="ml-1 text-xs text-rose-600">({renderErrors.count})</span>}
                    <NewBadge since="2026-04-27" featureId="route-health-render-errors" size="xs" />
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  {(renderErrors.errors || []).length === 0 ? (
                    <div className="px-3 py-6 text-center text-xs text-muted-foreground">No client-side render errors recorded — every page mounted cleanly.</div>
                  ) : (
                    <>
                      {(renderErrors.top_paths || []).length > 0 && (
                        <div className="px-3 py-2 border-b bg-rose-50/40 dark:bg-rose-950/20 text-[11px] flex flex-wrap gap-2" data-testid="render-error-top-paths">
                          {renderErrors.top_paths.map((tp) => (
                            <span key={tp.path} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-100 dark:bg-rose-900/40 text-rose-700 dark:text-rose-300 font-mono">
                              <span className="font-bold">{tp.count}×</span> {tp.path}
                            </span>
                          ))}
                        </div>
                      )}
                      <div className="divide-y max-h-[280px] overflow-y-auto">
                        {(renderErrors.errors || []).map((er, i) => (
                          <div key={er.id || i} className="px-3 py-2 text-[11px]" data-testid={`render-error-${i}`}>
                            <div className="flex items-center justify-between">
                              <span className="font-mono font-bold text-rose-700 break-all">{er.path || '/'}</span>
                              <span className="text-[10px] text-muted-foreground whitespace-nowrap ml-2">{new Date(er.reported_at).toLocaleString()}</span>
                            </div>
                            <div className="font-mono text-rose-600 mt-0.5 break-all">{er.message}</div>
                            <div className="text-[10px] text-muted-foreground mt-0.5">build: {er.build_id || 'unknown'}</div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
